import type { QueryClient } from "@tanstack/react-query";
import type { TelemetryOutageSummary } from "../data/types";

export interface TelemetryOutagePageOutageEvent {
  facilityId: number;
  isOngoing: boolean;
}

/**
 * Direct-patches only isOngoing on the per-facility Telemetry Outage page's
 * cached summary (useTelemetryOutages) from a live event:outage push
 * (JAA-1095), then always triggers a targeted refetch of the rest — on both
 * open and close.
 *
 * outageWindowStart/causeAtDetection are deliberately NOT direct-patched
 * here, unlike isOngoing — found in BE review: the REST API returns
 * outage_window_start in the facility's local timezone, but the live
 * event:outage payload sends it in UTC (Z-suffixed). Writing that raw UTC
 * value into the same cache field formatOutageTime reads naively (no TZ
 * conversion) could shift the displayed hour by several hours, and the
 * date near midnight. isOngoing has no such risk (a plain boolean), so it's
 * still patched instantly for the banner; the timestamp/cause are left for
 * the REST refetch below to fill in correctly.
 *
 * The 7-day aggregate metrics and paginated outage history can't be
 * direct-patched at all regardless: the live event doesn't carry enough to
 * reconstruct a new history row (endTime, status, information,
 * failedAttempts) or recompute rolling 7-day aggregates, and the history
 * table's pagination needs REST regardless. Polling for them was removed
 * anyway — instead, this always calls invalidateQueries for the facility's
 * telemetryOutages query, on both open and close (not just recovery — an
 * open also needs the REST refetch to get the correctly-timezoned
 * outageWindowStart). Its default refetchType ("active") means that only
 * actually fires a REST call if this exact facility's outage page is
 * currently mounted/observed; otherwise it just marks the cache stale for
 * next visit — no custom "is this page open" tracking needed, React Query
 * already knows.
 */
export function patchTelemetryOutagePageCache(
  queryClient: QueryClient,
  outage: TelemetryOutagePageOutageEvent,
): void {
  queryClient.setQueriesData<TelemetryOutageSummary>(
    { queryKey: ["telemetryOutages", outage.facilityId] },
    (data) => {
      if (!data) return data;
      return { ...data, isOngoing: outage.isOngoing };
    },
  );

  queryClient.invalidateQueries({
    queryKey: ["telemetryOutages", outage.facilityId],
  });
}
